import { Component } from 'react'
import { IClientUID } from '../../../api/clients'
import { getClientLogFile } from '../../../api/0x0001/logs'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import classNames from 'classnames'
import { getLocale, getString } from '../../../locale'
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineChevronDoubleLeft, HiOutlineChevronDoubleRight } from 'react-icons/hi'
import { format } from 'date-fns'

const LOGS_PER_PAGE = 40

enum LogType {
    I = 'info',
    W = 'warn',
    E = 'error',
    D = 'debug',
}

const TYPE_COLOR = {
    [LogType.I]: 'text-green-500',
    [LogType.W]: 'text-yellow-500',
    [LogType.E]: 'text-red-500',
    [LogType.D]: 'text-blue-500',
}

type ValidLogLine = {
    valid: boolean
    type: LogType
    date: Date
    message: string
}

type InvalidLogLine = {
    valid: boolean
    rawText: string
}

type LogLine = ValidLogLine | InvalidLogLine

type Props = {
    clientId: IClientUID
}

type State = {
    loadingFileContent: boolean
    logContent: LogLine[]
    logContentSlice: LogLine[]
    pages: number
    page: number
    showSysLogs: boolean
    showAll: boolean
}

class Logs extends Component<Props, State> {
    state: State = {
        loadingFileContent: false,
        logContent: [],
        logContentSlice: [],
        pages: 1,
        page: 1,
        showSysLogs: false,
        showAll: false,
    }

    componentDidMount = () => this.loadLogs()

    async loadLogs() {
        this.setState({ loadingFileContent: true })
        try {
            const { showSysLogs } = this.state

            const content = (await getClientLogFile(this.props.clientId, showSysLogs)).data

            const logContent = this.parseLog(content, showSysLogs)

            this.setState(
                {
                    logContent,
                },
                () => {
                    const pages = Math.ceil(logContent.length / LOGS_PER_PAGE)
                    const page = pages
                    const logContentSlice = this.calculateLogContentSlice(page)

                    this.setState({ logContentSlice, pages, page })
                },
            )
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
        this.setState({ loadingFileContent: false })
    }

    calculateLogContentSlice(selectedPage: number) {
        const startIndex = (selectedPage - 1) * LOGS_PER_PAGE
        const endIndex = startIndex + LOGS_PER_PAGE
        const newLogContentSlice = this.state.logContent.slice(startIndex, endIndex)

        return newLogContentSlice
    }

    parseLog(logFile: string, isSysLog: boolean): LogLine[] {
        const s = logFile.split('\n')

        const lines: LogLine[] = []
        for (const l of s) {
            if (l.length < 4) continue

            const ls = l.split('$')

            let type: LogType
            let date: Date
            let message: string

            try {
                if (isSysLog) {
                    //5$I (6432) AdcReader: Characterized using Default Vref
                    date = new Date(parseInt(ls[0]) * 1000)
                    type = LogType[ls[1].charAt(0) as keyof typeof LogType]
                    message = ls[1].substring(2, ls[1].length)
                } else {
                    type = LogType[ls[0] as keyof typeof LogType]
                    date = new Date(parseInt(ls[1]) * 1000)
                    message = ls[2]
                }

                lines.push({ valid: true, type, date, message })
            } catch {
                lines.push({ valid: false, rawText: l })
            }
        }

        return lines
    }

    handleLogFile = async (showSysLogs: boolean) => {
        this.setState({ showSysLogs }, () => this.loadLogs())
    }

    render() {
        return (
            <div className="flex flex-col md:flex-row-reverse gap-y-5 md:gap-x-3">
                {this.renderPanel()}
                {this.renderLogFile()}
            </div>
        )
    }

    renderPanel() {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-3 text-left relative">
                <div className="sticky top-5">
                    {this.renderLogFileOptions()}
                    {this.renderShowAll()}
                </div>
            </div>
        )
    }

    renderShowAll() {
        const { showAll, loadingFileContent } = this.state
        return (
            <div className="rounded bg-gray-300 dark:bg-gray-700 mt-5">
                <div className="flex items-center px-2 py-3">
                    <input
                        id="show-all"
                        type="checkbox"
                        className="border-gray-300 me-3"
                        checked={showAll}
                        onChange={({ target }) => this.setState({ showAll: target.checked })}
                        disabled={loadingFileContent}
                    />
                    <label htmlFor="show-all" className="dark:text-white text-sm">
                        {getString('logs.show_all')}
                    </label>
                </div>
            </div>
        )
    }

    renderLogFileOptions() {
        // print a 2 option radio one is machine logs and the other is system logs
        const { showSysLogs, loadingFileContent } = this.state
        return (
            <div className="rounded bg-gray-300 dark:bg-gray-700">
                <div className="flex items-center px-2 py-3">
                    <input
                        id="machine-logs"
                        type="checkbox"
                        className="border-gray-300 me-3"
                        checked={!showSysLogs}
                        onChange={() => this.handleLogFile(false)}
                        disabled={loadingFileContent}
                    />
                    <label htmlFor="machine-logs" className="dark:text-white text-sm">
                        {getString('logs.machine_logs')}
                    </label>
                </div>
                <div className="flex items-center px-2 py-3">
                    <input
                        id="sys-logs"
                        type="checkbox"
                        className="border-gray-300 me-3"
                        checked={showSysLogs}
                        onChange={() => this.handleLogFile(true)}
                        disabled={loadingFileContent}
                    />
                    <label htmlFor="sys-logs" className="dark:text-white text-sm">
                        {getString('logs.sys_logs')}
                    </label>
                </div>
            </div>
        )
    }

    renderLogFile() {
        return (
            <div className="flex-grow flex items-center justify-center bg-white dark:bg-gray-800 rounded-xl shadow-md p-3">
                {this.renderLogContent()}
            </div>
        )
    }

    renderLogContent() {
        const { loadingFileContent, showAll, logContent, logContentSlice, pages, page } = this.state
        if (loadingFileContent) return <Loader size={12} className="my-5" innerClassName="mx-auto" />

        const content = showAll ? logContent : logContentSlice

        return (
            <div className="w-full">
                {content.length === 0 && <div className="flex items-center justify-center text-white h-60">{getString('logs.empty_logs')}</div>}
                <div className="ltr">{content.map(this.renderLogLine.bind(this))}</div>

                {!showAll && (
                    <div className="mt-5 flex justify-between items-center gap-x-2">
                        {this.renderPageOption(
                            <div className="flex items-center text-xs">
                                <HiOutlineChevronDoubleLeft className={classNames(getLocale() === 'he' && 'flip-h')} />
                                <span className="hidden lg:block">{getString('common.first_page')}</span>
                            </div>,
                            () => {
                                this.setState({
                                    logContentSlice: this.calculateLogContentSlice(1),
                                    page: 1,
                                })
                            },
                            page <= 1,
                        )}
                        {this.renderPageOption(
                            <div className="flex items-center text-xs">
                                <HiOutlineChevronLeft className={classNames(getLocale() === 'he' && 'flip-h')} />
                                <span className="hidden lg:block">{getString('common.previous')}</span>
                            </div>,
                            () => {
                                this.setState({
                                    logContentSlice: this.calculateLogContentSlice(page - 1),
                                    page: page - 1,
                                })
                            },
                            page <= 1,
                        )}
                        <div className="text-white mx-3 flex-shrink">{page}</div>
                        {this.renderPageOption(
                            <div className="flex items-center text-xs">
                                <span className="hidden lg:block">{getString('common.next')}</span>
                                <HiOutlineChevronRight className={classNames(getLocale() === 'he' && 'flip-h')} />
                            </div>,
                            () => {
                                this.setState({
                                    logContentSlice: this.calculateLogContentSlice(page + 1),
                                    page: page + 1,
                                })
                            },
                            page >= pages,
                        )}
                        {this.renderPageOption(
                            <div className="flex items-center text-xs">
                                <span className="hidden lg:block">{getString('common.last_page')}</span>
                                <HiOutlineChevronDoubleRight className={classNames(getLocale() === 'he' && 'flip-h')} />
                            </div>,
                            () => {
                                this.setState({
                                    logContentSlice: this.calculateLogContentSlice(pages),
                                    page: pages,
                                })
                            },
                            page >= pages,
                        )}
                    </div>
                )}
            </div>
        )
    }

    renderPageOption(content: JSX.Element, onClick: () => void, disabled = false) {
        return (
            <div
                className={classNames(
                    'flex items-center p-4 bg-white dark:bg-gray-700 dark:text-white shadow-md rounded-xl flex-grow lg:flex-grow-0',
                    disabled ? 'bg-gray-200 dark:bg-opacity-40 cursor-not-allowed' : 'hover-grow',
                )}
                onClick={disabled ? undefined : onClick}
            >
                <div className="flex-grow flex flex-row justify-center items-center lg:w-24">{content}</div>
            </div>
        )
    }

    renderLogLine(logLine: LogLine, index: number) {
        return (
            <div key={index} className="flex items-center text-xs dark:text-white py-2 md:py-1 border-gray-500 border-t first:border-t-0">
                {logLine.valid ? this.renderValidLogLine(logLine as ValidLogLine) : this.renderInvalidLogLine(logLine as InvalidLogLine)}
            </div>
        )
    }

    renderValidLogLine(logLine: ValidLogLine) {
        const { type, date, message } = logLine
        return (
            <>
                <div className={classNames('me-2 font-bold w-7', TYPE_COLOR[type])}>{type}</div>
                <div className="flex-grow me-2">{message}</div>
                <div className="text-gray-500">{format(date, 'HH:mm:ss dd/MM/yyyy')}</div>
            </>
        )
    }

    renderInvalidLogLine(logLine: InvalidLogLine) {
        const { rawText } = logLine
        return (
            <>
                <div className="flex-grow me-2">{rawText}</div>
                <div className="text-gray-500">{getString('logs.parse_failed')}</div>
            </>
        )
    }
}

export default Logs
